import React, { useState, useEffect } from "react";
import _ from "lodash";
import clsx from "clsx";
import { useTranslate } from "react-polyglot";
import { useHistory } from "react-router-dom";
import { makeStyles, Typography, Box } from "@material-ui/core";
import { fade } from "@material-ui/core/styles";
import checkLevel from "../../../assets/check_level.svg";
import IconLock from "../../../assets/Icons/ic_lock_2.svg";
import useCompanyTitle from "../../../hooks/useCompanyTitle.js";
import { useCourseProvider } from "../../../Providers/Data/CourseProvider.jsx";
import FeedbackDialog from "../../../SharedComponents/FeedbackDialog/FeedbackDialog.jsx";
import CourseSelect from "../../../SharedComponents/CourseSelect.jsx";
import { useBaseData } from "../../../Providers/Data/BaseDataProvider.jsx";
import FlexboxHorizontal from "../../../SharedComponents/FlexboxHorizontal.jsx";
import LinearProgressBar from "../../../SharedComponents/LinearProgressBar.jsx";
import EmptyLevelsMessage from "./components/EmptyLevelsMessage.jsx";

const useStyles = makeStyles((theme) => ({
  courseStatPageRoot: {
    padding: "16px 22px",
  },
  branchesDecks: {
    margin: 0,
    width: "100%",
    maxWidth: "unset",
    [theme.breakpoints.up("lg")]: {
      margin: "24px 0 0 20px",
      maxWidth: "865px",
    },
  },
  levelsMainBox: {
    maxWidth: 532,
    marginLeft: "auto",
    marginRight: "auto",
    marginTop: 16,
  },
  itemLevel: {
    alignItems: "center",
    marginBottom: 16,
    cursor: "pointer",
    position: "relative",
    "&::before": {
      content: '""',
      top: 104,
      left: 13,
      width: 1,
      height: 102,
      position: "absolute",
      borderRight: "2px dashed #909293",
    },
    "&:last-child::before": {
      content: "none",
    },
  },
  lockItemLevel: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  checkIconBox: {
    width: 28,
    height: 28,
    border: "2px dashed #909293",
    borderRadius: "50%",
    backgroundColor: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
  },
  learnedIconBox: {
    border: `2px solid ${fade("#226C36", 0.2)}`,
    backgroundColor: "#B0F2B6",
  },
  titleBox: {
    width: 484,
    backgroundColor: "#fff",
    borderRadius: 16,
    boxShadow: `0 2px 6px 0 ${fade("#000", 0.05)}`,
    padding: "20px 16px",
    marginLeft: 20,
  },
  nameAndProgress: {
    width: 320,
  },
  name: {
    color: "#1B1C1C",
    fontSize: 16,
    fontWeight: 500,
    lineHeight: 1.4,
    height: 68,
    marginBottom: 8,
    display: "-webkit-box",
    "-webkit-line-clamp": 3,
    "-webkit-box-orient": "vertical",
    overflow: "hidden",
  },
  imageBox: {
    width: 110,
    height: 110,
    borderRadius: 16,
    overflow: "hidden",
    marginLeft: 20,
    flexShrink: 0,
  },
  levelImage: {
    width: "100%",
    height: "100%",
    display: "block",
    objectFit: "cover",
    objectPosition: "center",
  },
  pointsTitle: {
    color: "#54606B",
    fontSize: 12,
    marginBottom: 6,
  },
  levelProgress: {
    "& .MuiLinearProgress-bar": {
      borderRadius: 4,
    },
    "& .MuiLinearProgress-barColorSecondary": {
      backgroundColor: "#B0F2B6",
    },
  },
  levelLock: {
    position: "absolute",
    right: 10,
    top: 10,
  },
}));

/** */
const CourseStartPage = () => {
  const classes = useStyles();
  const t = useTranslate();
  const { eventsToFeedback, setEventsToFeedback, changeLevel } = useCourseProvider();
  const { levelsAndReward } = useBaseData();
  const history = useHistory();
  const [isFeedbackOpen, setFeedbackOpen] = useState(false);

  useCompanyTitle("title.home");

  useEffect(() => {
    if (eventsToFeedback.length > 0) {
      setFeedbackOpen(true);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventsToFeedback]);

  /** */
  const closeFeedbackDialog = () => {
    const cloneEvent = _.cloneDeep(eventsToFeedback);
    cloneEvent.shift();
    setEventsToFeedback(cloneEvent);
    setFeedbackOpen(false);
  };
  /** */
  const getLearnedLevel = (level) => {
    return Math.round((level.havePoints / level.points) * 100) >= 100;
  };

  /** */
  const onClickLevel = (level) => {
    if (level.open) {
      changeLevel(level);
      history.push("/branches");
    }
  };

  return (
    <Box className={classes.courseStatPageRoot}>
      <CourseSelect />
      <EmptyLevelsMessage />
      {levelsAndReward?.levels?.length > 0 && (
        <Box className={classes.levelsMainBox}>
          {levelsAndReward?.levels.map((level) => (
            <FlexboxHorizontal
              key={level.id}
              className={clsx(classes.itemLevel, {
                [classes.lockItemLevel]: !level.open,
              })}
              onClick={() => onClickLevel(level)}
            >
              <Box className={`${classes.checkIconBox} ${getLearnedLevel(level) ? classes.learnedIconBox : ""}`}>
                {getLearnedLevel(level) && <img src={checkLevel} alt="checkLevel" width={18} height={18} />}
              </Box>
              <FlexboxHorizontal className={classes.titleBox} justifyContent="space-between">
                <Box className={classes.nameAndProgress}>
                  <Typography className={classes.name} color="primary">
                    {level.name}
                  </Typography>
                  <Box className={classes.progressBox}>
                    <Typography className={classes.pointsTitle} color="primary">
                      {`${t("done")} ${Math.round((level.havePoints / level.points) * 100)}%`}
                    </Typography>
                    <LinearProgressBar
                      className={classes.levelProgress}
                      variant="determinate"
                      value={(level.havePoints / level.points) * 100}
                      height={8}
                      color={getLearnedLevel(level) ? "secondary" : "primary"}
                      style={{
                        backgroundColor: "#F1F2F2",
                        borderRadius: 4,
                      }}
                      noborder
                    />
                  </Box>
                </Box>
                {level.image?.url && (
                  <Box className={classes.imageBox}>
                    <img className={classes.levelImage} src={level.image.url} alt="image of level" />
                  </Box>
                )}
              </FlexboxHorizontal>
              {!level.open && (
                <Box className={classes.levelLock}>
                  <img src={IconLock} alt="checkLevel" width={18} height={18} />
                </Box>
              )}
            </FlexboxHorizontal>
          ))}
        </Box>
      )}
      {eventsToFeedback?.length > 0 && (
        <FeedbackDialog
          data={eventsToFeedback[0]}
          instructionText={t("feedbackDialog.eventInstruction")}
          resource="event"
          onClose={closeFeedbackDialog}
          open={isFeedbackOpen}
        />
      )}
    </Box>
  );
};

export default CourseStartPage;
